import React, { FunctionComponent } from 'react'
import { DonutChart } from '../Visualizations/DonutChart'
import { NoData } from '../Icons/Icons'
import healthStyles from './HealthCheckBrief.module.css'
import styles from './BriefDonut.module.css'

/*
 * A brief's donut chart: the Partner Intelligence palette (greens, teals and blues into the AI purples, then
 * calmer tints), a legend on the right, and counts in its tooltips. With nothing to show (no segments, or every
 * one zero) it shows the same empty state as the bar charts: a blurred stand-in chart under a message.
 */

export const BRIEF_DONUT_COLOURS = [
  '#27aa27', // green
  '#32c2af', // teal
  '#4383f2', // blue
  '#8ec640', // lime
  '#7c53c0', // purple
  '#7cded1', // aqua
  '#5c6ac4', // indigo
  '#ab87ff', // lilac
  '#1b7f3b', // forest
  '#168c7e', // deep teal
  '#2f5fb3', // deep blue
  '#c5e38f', // pale lime
  '#9dbef7', // sky
  '#d2c1ff', // pale lilac
  '#5b636a', // slate
  '#b8c3cc', // grey
]

/* the stand-in donut's segments: [start, length] as percentages of the ring */
const GHOST_SEGMENTS: [number, number][] = [
  [0, 46],
  [46, 30],
  [76, 24],
]

interface Props {
  id: string
  title: string
  data: Array<[string, number]>
}

export const BriefDonut: FunctionComponent<Props> = ({ id, title, data }) => {
  const empty = data.length === 0 || data.every(([, count]) => !count)
  if (empty) {
    // as the bar charts' empty state: a pale stand-in chart, blurred, under the "waiting on activity" message
    return (
      <div className={styles.empty} data-testid={`${id}-empty`}>
        <svg viewBox='0 0 360 220' className={styles.ghost} aria-hidden='true'>
          {GHOST_SEGMENTS.map(([offset, length], i) => (
            <circle
              key={offset}
              cx='110'
              cy='110'
              r='76'
              pathLength={100}
              strokeDasharray={`${length} ${100 - length}`}
              strokeDashoffset={-offset}
              className={styles[`ghost${i}`]}
            />
          ))}
          {[0, 1, 2].map(i => (
            <g key={i} className={styles.ghostLegend}>
              <rect x='222' y={88 + i * 18} width='10' height='10' rx='2' />
              <rect x='240' y={90 + i * 18} width={[64, 48, 56][i]} height='6' rx='3' />
            </g>
          ))}
        </svg>
        <div className={healthStyles.noDataOverlay}>
          <i className='icon twentyfour dip'>
            <NoData />
          </i>
          <h5>No data available</h5>
          <p>We&apos;re waiting on activity or still processing.</p>
        </div>
      </div>
    )
  }
  return (
    <DonutChart
      id={id}
      title={title}
      data={data}
      size={{ height: 220 }}
      legend={{ show: true, position: 'right' }}
      rawValues
      colors={BRIEF_DONUT_COLOURS}
    />
  )
}
